import React, { useEffect, useState } from "react";
import { FaEdit, FaTimes } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import Toast from "../../components/Toast/Toast";
import {
  getVideosAPI,
  addVideoAPI,
  updateVideoAPI,
  deleteVideoAPI,
} from "../../utils/api";
import { normalizeVideoList } from "../../utils/videoListNormalize";
import Sidebar from "../Sidebar/Sidebar";
import AddVideoModal from "./components/AddVideoModal";
import PlaybackVideo from "./components/PlaybackVideo";
import VideoThumbnailButton from "./components/VideoThumbnailButton";
import "../Banner/Banner.css";
import "./Videos.css";

/** Match Laravel video mimes rule (filename check before upload). */
const ALLOWED_VIDEO_FILE_RE =
  /\.(mp4|mov|avi|wmv|flv|mpeg|mpg|m4v|3gp|3g2|mj2|webm)$/i;

/** Laravel often returns validation errors as { field: ["msg"] } — show readable text */
function messageFromApiResponse(message) {
  if (typeof message === "string") return message;
  if (message != null && typeof message === "object" && !Array.isArray(message)) {
    const parts = [];
    for (const [key, val] of Object.entries(message)) {
      if (Array.isArray(val)) {
        parts.push(`${key}: ${val.join("; ")}`);
      } else if (val != null && typeof val === "object") {
        parts.push(`${key}: ${JSON.stringify(val)}`);
      } else {
        parts.push(`${key}: ${String(val)}`);
      }
    }
    return parts.length ? parts.join(" ") : "Failed due to internal error";
  }
  return "Failed due to internal error";
}

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getVideosAPI({});

      if (response?.status === 200) {
        setVideos(normalizeVideoList(response?.data ?? []));
      } else if (response?.message) {
        setError(messageFromApiResponse(response.message));
        setVideos([]);
      } else {
        setError("Failed due to internal error");
        setVideos([]);
      }
    } catch {
      setError("Failed due to internal error");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  useEffect(() => {
    if (!previewVideo) return;
    const esc = (e) => {
      if (e.key === "Escape") setPreviewVideo(null);
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [previewVideo]);

  const handleAddNew = () => {
    setSelectedVideo(null);
    setIsEditMode(false);
    setShowModal(true);
    setSaveLoading(false);
  };

  const handleEdit = (video) => {
    setSelectedVideo(video);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVideo(null);
    setIsEditMode(false);
    setSaveLoading(false);
  };

  const handleSave = async ({
    videoFile,
    productId,
    productName: _productName,
  }) => {
    const fileForUpload = videoFile instanceof File ? videoFile : null;

    if (!isEditMode && !fileForUpload) {
      showToast("Please choose a video file.", "error");
      return;
    }

    if (fileForUpload && !ALLOWED_VIDEO_FILE_RE.test(fileForUpload.name)) {
      showToast(
        "Unsupported format. Use mp4, mov, avi, wmv, flv, mpeg, mpg, m4v, 3gp, 3g2, mj2, or webm.",
        "error"
      );
      return;
    }

    setSaveLoading(true);
    try {
      if (isEditMode && selectedVideo?._id) {
        const formData = new FormData();
        formData.append("video_id", String(selectedVideo._id));
        if (fileForUpload) {
          formData.append("video", fileForUpload);
        }
        if (productId) {
          formData.append("product_id", String(productId));
        }

        const response = await updateVideoAPI(formData);
        const ok =
          response?.status === 200 || response?.status === 201;
        if (ok) {
          await fetchVideos();
          handleCloseModal();
          showToast("Video updated successfully!", "success");
        } else if (response?.message) {
          showToast(messageFromApiResponse(response.message), "error");
        } else {
          showToast("Failed due to internal error", "error");
        }
      } else {
        const formData = new FormData();
        formData.append("video", fileForUpload);
        if (productId) {
          formData.append("product_id", String(productId));
        }

        const response = await addVideoAPI(formData);
        const ok =
          response?.status === 200 || response?.status === 201;
        if (ok) {
          await fetchVideos();
          handleCloseModal();
          showToast("Video added successfully!", "success");
        } else if (response?.message) {
          showToast(messageFromApiResponse(response.message), "error");
        } else {
          showToast("Failed due to internal error", "error");
        }
      }
    } catch {
      showToast("Failed due to internal error", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setVideoToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!videoToDelete?._id) {
        showToast("Invalid video data", "error");
        return;
      }
      const response = await deleteVideoAPI({ video_id: videoToDelete._id });

      if (response?.status === 200) {
        setPreviewVideo(null);
        setShowDeleteModal(false);
        setVideoToDelete(null);
        showToast("Video deleted successfully!", "success");
        await fetchVideos();
      } else if (response?.message) {
        showToast(messageFromApiResponse(response.message), "error");
      } else {
        showToast("Failed due to internal error", "error");
      }
    } catch {
      showToast("Failed due to internal error", "error");
    }
  };

  const openPreview = (video) => setPreviewVideo(video);
  const closePreview = () => setPreviewVideo(null);

  return (
    <>
      <Toast toast={toast} />

      <div className="Banner-layout">
        <Sidebar />
        <div className="Banner-main-content">
          <div className="Videos-header-row">
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Video"
            >
              <span>Add Video</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Loading videos...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div
                style={{
                  color: "#dc2626",
                  fontSize: "1.25rem",
                  marginBottom: "1rem",
                }}
              >
                ⚠️
              </div>
              <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
            </div>
          ) : videos.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📹</div>
              <h3>No Videos Found</h3>
              <p>There are no videos in the system yet.</p>
              <p>Click &quot;Add Video&quot; to create the first one.</p>
            </div>
          ) : (
            <div className="banner-grid">
              {videos.map((video, idx) => (
                  <div
                    key={video._id || `video-${idx}`}
                    className="banner-card video-card"
                  >
                    <div className="banner-image-container video-card-media">
                      {video.videoUrl?.trim() ? (
                        <VideoThumbnailButton
                          rawUrl={video.videoUrl}
                          onOpen={() => openPreview(video)}
                          ariaLabel={`Open video preview${video.productName ? ` — ${video.productName}` : ""}`}
                          fallbackClassName="video-card-fallback"
                        />
                      ) : (
                        <div className="video-card-fallback">
                          No video URL
                        </div>
                      )}
                      {video.videoUrl?.trim() ? (
                        <div
                          className="banner-actions-overlay video-card-actions"
                          role="presentation"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="edit-btn"
                            title="Edit Video"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(video);
                            }}
                            aria-label="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="delete-btn"
                            title="Delete Video"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(video);
                            }}
                            aria-label="Delete"
                          >
                            <MdDelete />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {video.productName?.trim() ? (
                      <div className="video-card-footer">
                        <div className="video-card-product">
                          {video.productName}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
            </div>
          )}

          {previewVideo?.videoUrl?.trim() && (
            <div
              className="video-fullscreen-overlay"
              role="presentation"
              onClick={closePreview}
            >
              <div
                className="video-fullscreen-box"
                role="dialog"
                aria-modal="true"
                aria-label="Video preview"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="video-fullscreen-player-wrap">
                  <button
                    type="button"
                    className="video-fullscreen-close"
                    aria-label="Close preview"
                    onClick={closePreview}
                  >
                    <FaTimes />
                  </button>
                  <PlaybackVideo
                    rawUrl={previewVideo.videoUrl}
                    className="video-fullscreen-player"
                    label={`Video preview${previewVideo.productName ? ` — ${previewVideo.productName}` : ""}`}
                    fallbackClassName="video-fullscreen-fallback"
                    showControls
                    autoPlay
                    loop
                  />
                </div>
              </div>
            </div>
          )}

          <AddVideoModal
            showModal={showModal}
            onClose={handleCloseModal}
            onSave={handleSave}
            saveLoading={saveLoading}
            isEditMode={isEditMode}
            selectedVideo={selectedVideo}
          />

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete this video?${
              videoToDelete?.productName
                ? ` (${videoToDelete.productName})`
                : ""
            }`}
            confirmText="Delete"
          />
        </div>
      </div>
    </>
  );
};

export default Videos;
